import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { API } from "../lib/api";
import { useCart } from "../context/CartContext";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 10;

const CheckoutSuccess = () => {
    const [search] = useSearchParams();
    const sessionId = search.get("session_id");
    const { clear } = useCart();
    const [state, setState] = useState({ phase: "polling", attempts: 0, data: null, error: null });
    const cartClearedRef = useRef(false);

    const poll = useCallback(
        async (attempt) => {
            if (!sessionId) {
                setState({ phase: "error", attempts: attempt, data: null, error: "Missing session." });
                return;
            }
            try {
                const res = await fetch(`${API}/checkout/status/${sessionId}`);
                if (!res.ok) throw new Error("Status check failed");
                const data = await res.json();

                if (data.payment_status === "paid") {
                    if (!cartClearedRef.current) {
                        cartClearedRef.current = true;
                        clear();
                    }
                    setState({ phase: "paid", attempts: attempt, data, error: null });
                    return;
                }
                if (data.status === "expired" || data.payment_status === "failed") {
                    setState({ phase: "failed", attempts: attempt, data, error: null });
                    return;
                }
                if (attempt + 1 >= MAX_ATTEMPTS) {
                    setState({ phase: "timeout", attempts: attempt + 1, data, error: null });
                    return;
                }
                setState({ phase: "polling", attempts: attempt + 1, data, error: null });
                setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
            } catch (e) {
                if (attempt + 1 >= MAX_ATTEMPTS) {
                    setState({ phase: "timeout", attempts: attempt + 1, data: null, error: e.message });
                    return;
                }
                setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
            }
        },
        [sessionId, clear],
    );

    useEffect(() => {
        poll(0);
    }, [poll]);

    return (
        <div data-testid="checkout-success-page" className="bg-[#FAF9F6] min-h-[70vh] px-6 lg:px-10 py-24">
            <div className="max-w-2xl mx-auto text-center">
                {state.phase === "polling" && (
                    <div data-testid="checkout-polling">
                        <Loader2 size={32} className="mx-auto animate-spin text-[#6A7059]" strokeWidth={1.3} />
                        <p className="label mt-6">Checking your payment</p>
                        <h1 className="font-serif text-4xl mt-4 leading-tight">
                            A moment, please.
                        </h1>
                        <p className="mt-5 text-[#4A4A46]">
                            We&rsquo;re quietly confirming your order with Stripe.
                        </p>
                    </div>
                )}

                {state.phase === "paid" && (
                    <div data-testid="checkout-paid">
                        <CheckCircle2 size={36} className="mx-auto text-[#6A7059]" strokeWidth={1.3} />
                        <p className="label mt-6">Order received</p>
                        <h1 className="font-serif text-5xl mt-4 leading-tight">
                            Thank you.
                        </h1>
                        <p className="mt-6 text-[#4A4A46] leading-relaxed">
                            Your bars are being gathered. We&rsquo;ll send a quiet email when
                            they ship.
                        </p>
                        {state.data?.order_number && (
                            <>
                                <p className="mt-10 label">Order Number</p>
                                <p
                                    className="mt-3 font-serif text-2xl"
                                    data-testid="order-number"
                                >
                                    {state.data.order_number}
                                </p>
                            </>
                        )}
                        <div className="mt-10">
                            <Link to="/shop" className="btn-secondary" data-testid="continue-shopping-cta">
                                Continue Shopping
                            </Link>
                        </div>
                    </div>
                )}

                {state.phase === "failed" && (
                    <div data-testid="checkout-failed">
                        <XCircle size={36} className="mx-auto text-[#1C1C1A]" strokeWidth={1.3} />
                        <p className="label mt-6">Payment not completed</p>
                        <h1 className="font-serif text-4xl mt-4">No payment was taken.</h1>
                        <p className="mt-6 text-[#4A4A46] leading-relaxed">
                            Your cart is still here whenever you&rsquo;re ready.
                        </p>
                        <div className="mt-10 flex gap-3 justify-center">
                            <Link to="/checkout" className="btn-primary" data-testid="retry-checkout-cta">
                                Try Again
                            </Link>
                            <Link to="/shop" className="btn-secondary">
                                Back to Shop
                            </Link>
                        </div>
                    </div>
                )}

                {state.phase === "timeout" && (
                    <div data-testid="checkout-timeout">
                        <p className="label">Still checking</p>
                        <h1 className="font-serif text-4xl mt-4 leading-tight">
                            Taking a moment.
                        </h1>
                        <p className="mt-6 text-[#4A4A46] leading-relaxed">
                            We haven&rsquo;t had Stripe confirm yet. If your card was charged,
                            you&rsquo;ll receive a confirmation email shortly. You can refresh
                            this page or check your inbox.
                        </p>
                        <div className="mt-10">
                            <button
                                onClick={() => {
                                    setState({ phase: "polling", attempts: 0, data: null, error: null });
                                    poll(0);
                                }}
                                className="btn-primary"
                                data-testid="recheck-status-button"
                            >
                                Check Again
                            </button>
                        </div>
                    </div>
                )}

                {state.phase === "error" && (
                    <div data-testid="checkout-error">
                        <XCircle size={36} className="mx-auto text-[#1C1C1A]" strokeWidth={1.3} />
                        <p className="label mt-6">Something is missing</p>
                        <h1 className="font-serif text-4xl mt-4 leading-tight">
                            {state.error || "Missing session."}
                        </h1>
                        <p className="mt-6 text-[#4A4A46] leading-relaxed">
                            We couldn&rsquo;t find a checkout session to confirm. Your cart is
                            still here whenever you&rsquo;re ready.
                        </p>
                        <div className="mt-10 flex gap-3 justify-center">
                            <Link to="/checkout" className="btn-primary" data-testid="error-back-to-checkout">
                                Back to Checkout
                            </Link>
                            <Link to="/shop" className="btn-secondary">
                                Shop Soap
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CheckoutSuccess;
